import * as React from 'react';

import { useAtom, useAtomValue } from 'jotai';

import {
  $isRightSidebarOpen,
  $rightSidebarType,
  $rightSidebarWidth,
  type RightSidebarType,
} from '~/app/state/rightSidebar.state';

interface RightSidebarContextType {
  // State
  sidebarType: RightSidebarType;
  isOpen: boolean;
  sidebarWidth: number; // Width in pixels

  // Actions
  openChat: () => void;
  openAssistant: () => void;
  close: () => void;
  toggle: (type: 'chat' | 'assistant') => void;
}

const RightSidebarContext = React.createContext<
  RightSidebarContextType | undefined
>(undefined);

export const RightSidebarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [sidebarType, setSidebarType] = useAtom($rightSidebarType);
  const isOpen = useAtomValue($isRightSidebarOpen);
  const sidebarWidth = useAtomValue($rightSidebarWidth);

  const openChat = React.useCallback(() => {
    setSidebarType('chat');
  }, [setSidebarType]);

  const openAssistant = React.useCallback(() => {
    setSidebarType('assistant');
  }, [setSidebarType]);

  const close = React.useCallback(() => {
    setSidebarType('none');
  }, [setSidebarType]);

  const toggle = React.useCallback(
    (type: 'chat' | 'assistant') => {
      setSidebarType((current) => (current === type ? 'none' : type));
    },
    [setSidebarType],
  );

  return (
    <RightSidebarContext.Provider
      value={{
        sidebarType,
        isOpen,
        sidebarWidth,
        openChat,
        openAssistant,
        close,
        toggle,
      }}
    >
      {children}
    </RightSidebarContext.Provider>
  );
};

export const useRightSidebar = () => {
  const context = React.useContext(RightSidebarContext);
  if (context === undefined) {
    throw new Error(
      'useRightSidebar must be used within a RightSidebarProvider',
    );
  }
  return context;
};

// Convenience hooks for backward compatibility
export const useChatSidebar = () => {
  const { sidebarType, toggle, openChat, close, sidebarWidth } =
    useRightSidebar();
  return {
    isChatOpen: sidebarType === 'chat',
    toggleChat: () => toggle('chat'),
    openChat,
    closeChat: close,
    sidebarWidth: sidebarType === 'chat' ? sidebarWidth : 0,
  };
};

export const useAssistantSidebar = () => {
  const { sidebarType, toggle, openAssistant, close, sidebarWidth } =
    useRightSidebar();
  return {
    isAssistantOpen: sidebarType === 'assistant',
    toggleAssistant: () => toggle('assistant'),
    openAssistant,
    closeAssistant: close,
    sidebarWidth: sidebarType === 'assistant' ? sidebarWidth : 0,
  };
};
